// Iter102D — Square Web Payments SDK on REAL /checkout/pay.
//
// Square is the PRIMARY customer-facing payment method for Lifted at the
// Moment. PayPal is the secondary option — the "PayPal" chip is rendered
// only when the deployment surfaces PayPal via /api/checkout/processor
// (paypal_active=true). Until existing PayPal production credentials
// are recovered no dead PayPal button is displayed.
//
// Flow:
//   /checkout (address + totals) → POST /api/checkout/create (lock totals)
//   → /checkout/pay?checkout_id=... (this page)
//   → Square Web Payments tokenizes card client-side
//   → POST /api/checkout/square/pay with { source_id, checkout_id, idempotency_key }
//   → server creates Square order + payment, writes delivery_orders row
//   → success redirects to /account/orders/{order_id}?success=true
//
// IMPORTANT: never expose SQUARE_ACCESS_TOKEN to the browser. Only the
// public REACT_APP_SQUARE_APPLICATION_ID and REACT_APP_SQUARE_LOCATION_ID
// are read here. Card details never touch our servers — Square returns a
// single-use source_id which is the only thing we POST.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, CreditCard, Lock, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION = process.env.REACT_APP_SQUARE_LOCATION_ID;

// Customer-safe Square decline message map. Must match backend
// SQUARE_CUSTOMER_MESSAGES in lifted/square_payments.py.
const SQUARE_ERROR_MESSAGES = {
  CARD_DECLINED: "Your card was declined. Please try another card or contact your card provider.",
  CARDHOLDER_INSUFFICIENT_PERMISSIONS: "This card could not be approved. Please try another card or contact your card provider.",
  INSUFFICIENT_FUNDS: "This card has insufficient funds. Please try another payment method.",
  VERIFY_CVV_FAILURE: "The security code did not match. Please check your card details and try again.",
  CVV_FAILURE: "The security code did not match. Please check your card details and try again.",
  VERIFY_AVS_FAILURE: "The billing ZIP code did not match. Please check your ZIP code and try again.",
  ADDRESS_VERIFICATION_FAILURE: "The billing address could not be verified. Please check it and try again.",
  INVALID_EXPIRATION: "The card expiration date is invalid. Please check your card and try again.",
  INVALID_CARD: "Your card details could not be verified. Please check the number and try again.",
  INVALID_CARD_DATA: "Your card details could not be verified. Please check the number and try again.",
  EXPIRED_CARD: "This card has expired. Please try another payment method.",
  VALUE_TOO_LONG: "Something went wrong while processing your payment. Please try again.",
  INVALID_REQUEST_ERROR: "Something went wrong while processing your payment. Please try again.",
  PAYMENT_LIMIT_EXCEEDED: "This payment could not be completed. Please try another payment method.",
  TRANSACTION_LIMIT: "This payment exceeds the transaction limit. Please try another payment method.",
  GENERIC_DECLINE: "This payment was declined. Please try another payment method.",
  CARD_NOT_SUPPORTED: "This card type is not supported. Please try another payment method.",
};
const GENERIC_PAYMENT_ERROR = "We couldn't complete your card payment. Please verify your information, use another card, or select PayPal.";

function getCustomerPaymentError(payloadOrErr) {
  if (typeof payloadOrErr?.customer_message === "string" && payloadOrErr.customer_message) {
    return payloadOrErr.customer_message;
  }
  if (typeof payloadOrErr?.response?.data?.customer_message === "string"
      && payloadOrErr.response.data.customer_message) {
    return payloadOrErr.response.data.customer_message;
  }
  const code = payloadOrErr?.error_code
    || payloadOrErr?.response?.data?.error_code
    || payloadOrErr?.code
    || payloadOrErr?.errors?.[0]?.code;
  if (code && SQUARE_ERROR_MESSAGES[code]) return SQUARE_ERROR_MESSAGES[code];
  return GENERIC_PAYMENT_ERROR;
}

export default function SquareCheckout() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id");
  const email = params.get("email") || "";

  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("square"); // "square" | "paypal"
  const [processor, setProcessor] = useState(null); // /api/checkout/processor flags
  const [methodErrors, setMethodErrors] = useState({});
  const cardRef = useRef(null);
  const applePayRef = useRef(null);
  const cashAppRef = useRef(null);
  const paymentsRef = useRef(null);
  // Stable idempotency key per checkout — reused across double-click / retry
  // so backend `/checkout/square/pay` dedupes at Square API level.
  const idempotencyKeyRef = useRef(null);
  if (checkoutId && !idempotencyKeyRef.current) {
    idempotencyKeyRef.current = `latm-${checkoutId}-${Date.now()}`;
  }

  // Load checkout draft (locked totals)
  useEffect(() => {
    if (!checkoutId) {
      setError("Missing checkout_id. Please return to your cart and try again.");
      return;
    }
    api.get(`/checkout/${checkoutId}`)
      .then((r) => setDraft(r.data))
      .catch(() => setError("We could not load your checkout. Please return to your cart and try again."));
  }, [checkoutId]);

  // Load processor flags so we know whether PayPal secondary is enabled.
  useEffect(() => {
    api.get("/checkout/processor")
      .then((r) => setProcessor(r.data || {}))
      .catch(() => setProcessor({}));
  }, []);

  // Inject Square Web Payments SDK
  useEffect(() => {
    if (window.Square) { setSdkReady(true); return; }
    const env = process.env.REACT_APP_SQUARE_ENVIRONMENT || "production";
    const src = env === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => setSdkReady(true);
    s.onerror = () => setError("Could not load Square secure card processing. Please refresh and try again.");
    document.body.appendChild(s);
  }, []);

  const totalCents = draft
    ? (draft.total_cents
        ? Number(draft.total_cents)
        : Math.round(Number(draft.total_amount || 0) * 100))
    : 0;
  const totalUsd = (totalCents / 100).toFixed(2);

  // Order summary line items
  const items = useMemo(() => (draft?.items || []), [draft]);
  const shipAddr = draft?.shipping_address || draft?.address || null;

  // Initialize Square payment fields (single-attach guarded by ref so
  // re-renders don't stack duplicate card widgets).
  const attachedRef = useRef(false);
  useEffect(() => {
    if (!sdkReady || !draft || !SQUARE_APP_ID || !SQUARE_LOCATION) return;
    if (selectedMethod !== "square") return;
    if (attachedRef.current) return;
    attachedRef.current = true;
    let card;
    (async () => {
      try {
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION);
        paymentsRef.current = payments;

        card = await payments.card();
        if (cardRef.current) {
          // Clear any prior card iframe injected by a StrictMode double-mount
          try { cardRef.current.innerHTML = ""; } catch { /* noop */ }
          await card.attach(cardRef.current);
          cardRef.current.__card = card;
        }

        // Cash App Pay (secondary Square wallet)
        try {
          const paymentRequest = payments.paymentRequest({
            countryCode: "US",
            currencyCode: "USD",
            total: { amount: (totalCents / 100).toFixed(2), label: "Lifted At The Moment" },
          });
          const cap = await payments.cashAppPay(paymentRequest, {
            redirectURL: window.location.href,
            referenceId: checkoutId,
          });
          if (cashAppRef.current) {
            await cap.attach(cashAppRef.current);
            cap.addEventListener("ontokenization", async (e) => {
              const { tokenResult } = e?.detail || {};
              if (tokenResult?.status === "OK") {
                await chargeWithSource(tokenResult.token, "cash_app_pay");
              } else {
                setError("Cash App Pay could not authorize this payment. Please try again or use a card.");
              }
            });
            cashAppRef.current.__obj = cap;
          }
        } catch (e) {
          setMethodErrors((m) => ({ ...m, cashapp: e?.message || String(e) }));
        }

        // Apple Pay (optional — behind REACT_APP_ENABLE_APPLE_PAY)
        if (String(process.env.REACT_APP_ENABLE_APPLE_PAY).toLowerCase() === "true") {
          try {
            const apreq = payments.paymentRequest({
              countryCode: "US",
              currencyCode: "USD",
              total: { amount: (totalCents / 100).toFixed(2), label: "Lifted At The Moment" },
            });
            const ap = await payments.applePay(apreq);
            if (applePayRef.current) {
              await ap.attach(applePayRef.current);
              applePayRef.current.__obj = ap;
              applePayRef.current.addEventListener("click", async () => {
                try {
                  const result = await ap.tokenize();
                  if (result.status === "OK") {
                    await chargeWithSource(result.token, "apple_pay");
                  } else {
                    setError(result?.errors?.[0]?.message || "Apple Pay tokenization failed");
                  }
                } catch (err) {
                  setError(err?.message || "Apple Pay failed");
                }
              });
            }
          } catch (e) {
            setMethodErrors((m) => ({ ...m, applepay: e?.message || String(e) }));
          }
        }
      } catch (e) {
        setError(`Square secure card processing could not initialize: ${e.message}`);
      }
    })();
    return () => { try { card?.destroy(); } catch { /* ignore */ } };
  }, [sdkReady, draft, checkoutId, totalCents, selectedMethod]);

  async function chargeWithSource(sourceId, method = "card") {
    if (busy) return; // prevent double-submit
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/checkout/square/pay", {
        source_id: sourceId,
        checkout_id: checkoutId,
        customer_email: email,
        payment_method: method,
        // Stable idempotency key — reused on retry/double-click so Square
        // + backend dedupe on the same key.
        idempotency_key: idempotencyKeyRef.current,
      });
      if (data?.ok) {
        // Clear cart before redirect
        try { localStorage.removeItem("latm_cart_v1"); } catch { /* noop */ }
        navigate(`/account/orders/${data.order_id}?success=true`);
      } else {
        setError(getCustomerPaymentError(data));
        setBusy(false);
      }
    } catch (e) {
      console.error("[square] charge failed", e?.response?.data || e);
      setError(getCustomerPaymentError(e));
      setBusy(false);
    }
  }

  async function payWithCard() {
    if (busy) return;
    try {
      const card = cardRef.current?.__card;
      if (!card) throw new Error("Card form is not ready yet — please wait a moment and try again.");
      setBusy(true);
      const result = await card.tokenize();
      if (result.status !== "OK") {
        setBusy(false);
        const sqMsg = result?.errors?.[0]?.message;
        if (sqMsg && !/unknown/i.test(sqMsg)) {
          setError(sqMsg);
          return;
        }
        setError("We could not read your card details. Please check the number, expiration, CVV and try again.");
        return;
      }
      await chargeWithSource(result.token, "card");
    } catch (e) {
      setError(e.message || GENERIC_PAYMENT_ERROR);
      setBusy(false);
    }
  }

  const paypalAvailable = !!(processor?.paypal_active && processor?.paypal_configured);

  return (
    <div className="min-h-screen px-4 py-8 sm:py-10 max-w-6xl mx-auto" data-testid="square-checkout-page">
      <Link to="/checkout" className="inline-flex items-center gap-1 text-xs text-lifted-dim hover:text-white mb-4" data-testid="sc-back-link">
        <ArrowLeft className="h-3 w-3" /> Back to shipping
      </Link>
      <div className="eyebrow flex items-center gap-2 mb-2">
        <Lock className="h-3 w-3" /> Secure checkout · Step 2 of 2
      </div>
      <h1 className="font-anton uppercase text-3xl sm:text-4xl text-white mb-6" data-testid="sc-title">Payment</h1>

      {error && (
        <div className="glass-panel p-3 mb-4 border border-rose-500/40 text-rose-200 text-sm"
             data-testid="square-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* LEFT: Payment method selector + widgets */}
        <div className="space-y-4">
          {/* Primary: SQUARE */}
          <div
            className={`glass-panel p-4 border transition ${
              selectedMethod === "square"
                ? "border-lifted-green/60 bg-lifted-green/5"
                : "border-white/10"
            }`}
            data-testid="sc-method-square-card"
          >
            <label className="flex items-start gap-3 cursor-pointer" data-testid="sc-method-square-label">
              <input
                type="radio"
                name="pm"
                checked={selectedMethod === "square"}
                onChange={() => setSelectedMethod("square")}
                className="mt-1 accent-lifted-green"
                data-testid="sc-method-square-radio"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-lifted-green" />
                  <span className="font-bebas tracking-[0.18em] uppercase text-sm text-white">Credit / Debit Card</span>
                  <span className="ml-auto text-[10px] font-bebas tracking-[0.22em] uppercase text-lifted-green">Primary</span>
                </div>
                <div className="text-[11px] text-lifted-dim mt-0.5">
                  Secure payment through Square. Card details never touch our servers.
                </div>
              </div>
            </label>

            {selectedMethod === "square" && (
              <div className="mt-4 space-y-3" data-testid="square-form">
                <div ref={cardRef} id="square-card" data-testid="square-card-element"
                     className="bg-black/40 rounded p-3 min-h-[110px]" />
                <button
                  onClick={payWithCard}
                  disabled={busy || !sdkReady || !draft}
                  className="btn-lifted w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="square-pay-button"
                >
                  {busy
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing Payment…</>
                    : <><ShieldCheck className="h-4 w-4" /> Pay ${totalUsd}</>}
                </button>

                <div className="text-[10px] text-center text-lifted-dim uppercase tracking-[0.2em] my-2">
                  — or pay with —
                </div>

                <div ref={cashAppRef} data-testid="square-cashapp" id="cash-app-pay" />
                {methodErrors.cashapp && (
                  <div className="text-[10px] text-amber-300/80" data-testid="square-cashapp-error">
                    Cash App Pay unavailable on this device.
                  </div>
                )}
                {String(process.env.REACT_APP_ENABLE_APPLE_PAY).toLowerCase() === "true" && (
                  <>
                    <div ref={applePayRef} data-testid="square-applepay" id="apple-pay"
                         className="apple-pay-button" style={{ minHeight: 44 }} />
                    {methodErrors.applepay && (
                      <div className="text-[10px] text-amber-300/80" data-testid="square-applepay-error">
                        Apple Pay unavailable on this device.
                      </div>
                    )}
                  </>
                )}

                <p className="text-[0.65rem] text-lifted-dim text-center mt-3 flex items-center justify-center gap-1">
                  <Lock className="h-3 w-3" /> Secure card processing by Square. We never see your card details.
                </p>
              </div>
            )}
          </div>

          {/* Secondary: PAYPAL (only shown when backend reports paypal_active) */}
          {paypalAvailable && (
            <div
              className={`glass-panel p-4 border transition ${
                selectedMethod === "paypal"
                  ? "border-lifted-green/60 bg-lifted-green/5"
                  : "border-white/10"
              }`}
              data-testid="sc-method-paypal-card"
            >
              <label className="flex items-start gap-3 cursor-pointer" data-testid="sc-method-paypal-label">
                <input
                  type="radio"
                  name="pm"
                  checked={selectedMethod === "paypal"}
                  onChange={() => setSelectedMethod("paypal")}
                  className="mt-1 accent-lifted-green"
                  data-testid="sc-method-paypal-radio"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bebas tracking-[0.18em] uppercase text-sm text-white">PayPal</span>
                    <span className="ml-auto text-[10px] font-bebas tracking-[0.22em] uppercase text-lifted-dim">Secondary</span>
                  </div>
                  <div className="text-[11px] text-lifted-dim mt-0.5">
                    Pay securely with your PayPal balance or linked card.
                  </div>
                </div>
              </label>
              {selectedMethod === "paypal" && (
                <div className="mt-4" data-testid="sc-paypal-slot" id="paypal-buttons">
                  {/* PayPal Smart Buttons render target — populated by the
                      PayPal SDK once real production credentials are wired
                      into REACT_APP_PAYPAL_CLIENT_ID + backend paypal_active. */}
                  <p className="text-[11px] text-amber-300/80">
                    PayPal is loading…
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Order summary */}
        <aside className="glass-panel p-4 lg:sticky lg:top-6" data-testid="sc-order-summary">
          <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase text-lifted-green mb-3">
            Order summary
          </div>
          {items.length > 0 ? (
            <ul className="space-y-3 mb-4" data-testid="sc-line-items">
              {items.map((li, idx) => {
                const key = li.cart_key || li.slug || li.handle || `li-${idx}`;
                const qty = Number(li.quantity || li.qty || 1);
                const unit = Number(li.unit_price_cents || li.price_cents || 0);
                const lineTotal = (qty * unit) / 100;
                const img = li.image_url || li.image || li.thumbnail || null;
                return (
                  <li key={key} className="flex gap-3" data-testid={`sc-line-${key}`}>
                    <div className="w-12 h-12 rounded bg-black/50 overflow-hidden flex-shrink-0 relative">
                      {img
                        ? <img src={img} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-lifted-green/10" />}
                      <span className="absolute -top-1 -right-1 text-[9px] bg-lifted-green text-black px-1.5 py-0.5 rounded-full font-bebas">
                        {qty}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate">{li.title || li.handle || "Item"}</div>
                      {li.variant_title && (
                        <div className="text-[10px] text-lifted-green font-bebas tracking-[0.18em] uppercase truncate" data-testid={`sc-variant-${key}`}>
                          {li.variant_title}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-white flex-shrink-0">${lineTotal.toFixed(2)}</div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-xs text-lifted-dim italic mb-4">Loading items…</div>
          )}

          <div className="space-y-1 text-sm">
            {draft && (
              <>
                <SummaryLine label="Subtotal" value={`$${Number(draft.subtotal || 0).toFixed(2)}`} testid="sc-sum-subtotal" />
                {Number(draft.shipping_fee || 0) > 0 && (
                  <SummaryLine label="Shipping" value={`$${Number(draft.shipping_fee).toFixed(2)}`} testid="sc-sum-shipping" />
                )}
                {Number(draft.delivery_fee || 0) > 0 && (
                  <SummaryLine label="Same-day delivery" value={`$${Number(draft.delivery_fee).toFixed(2)}`} testid="sc-sum-delivery" />
                )}
                {Number(draft.tax_fee || 0) > 0 && (
                  <SummaryLine label={`Tax${draft.tax_state ? ` (${draft.tax_state})` : ""}`} value={`$${Number(draft.tax_fee).toFixed(2)}`} testid="sc-sum-tax" />
                )}
                {Number(draft.discount || 0) > 0 && (
                  <SummaryLine label="Discount" value={`-$${Number(draft.discount).toFixed(2)}`} testid="sc-sum-discount" />
                )}
              </>
            )}
          </div>
          <hr className="border-white/10 my-3" />
          <div className="flex justify-between items-baseline" data-testid="sc-sum-total">
            <span className="font-bebas tracking-[0.18em] uppercase text-xs">Total</span>
            <span className="font-anton text-2xl text-lifted-green" data-testid="square-total-amount">
              ${totalUsd} USD
            </span>
          </div>

          {shipAddr && (
            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-lifted-dim" data-testid="sc-ship-summary">
              <div className="font-bebas tracking-[0.18em] uppercase text-[9px] text-lifted-green mb-1">Ship to</div>
              <div className="text-white truncate">{shipAddr.name || shipAddr.full_name || draft?.customer_name}</div>
              <div className="truncate">
                {shipAddr.line1 || shipAddr.address_line1}
                {shipAddr.line2 || shipAddr.address_line2 ? `, ${shipAddr.line2 || shipAddr.address_line2}` : ""}
              </div>
              <div className="truncate">
                {shipAddr.city}, {shipAddr.state} {shipAddr.zip || shipAddr.postal_code}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function SummaryLine({ label, value, testid }) {
  return (
    <div className="flex justify-between items-baseline" data-testid={testid}>
      <span className="text-lifted-dim text-xs">{label}</span>
      <span className="text-white text-xs">{value}</span>
    </div>
  );
}
